import React, { useCallback, useState, useEffect, useRef } from "react";
import { Form, Row, Col, Select, Input, message, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from 'dayjs'
import { getCaseTags } from "../../../api/CaseTagsAPI"
import { StyledButton, StyledRangePicker, StyledCheckbox, ClearAllContainer } from "./StyledComponents"
import { usePhysicians } from "../../../hooks/usePhysicians";
import { LocalStorageEnum, TimePeriodEnum } from "../../../constants/enums";
import { useLocalStorage } from '../../../hooks/useLocalStorage';


const { Option } = Select;

const { DAY, HOUR } = TimePeriodEnum;

const DateRangePicker = ({ value, onChange, ...props }) => {
  const momentValue = Array.isArray(value)
    ? value.map(v => (v ? dayjs(v) : null))
    : undefined;
  return <StyledRangePicker value={momentValue} onChange={onChange} {...props} />;
};

const UnhandledDefault = {
  SPAN: 2,
  INCREMENT: DAY,
}

const CaseFilter = ({
  clients,
  states,
  telemedicineCategory,
  caseStatus,
  onFilter,
  clearFilter,
  form,
  setCases,
  setSearchFilter,
}) => {
  const [availableCaseTags, setAvailableCaseTags] = useState([])
  const [caseFilters] = useLocalStorage(LocalStorageEnum.CASE_FILTERS, {});
  const [caseSearchResults] = useLocalStorage(LocalStorageEnum.CASE_SEARCH_RESULTS, []);
  const hasRestoredFilters = useRef(false);
  
  const { physicians } = usePhysicians();
  // Add filters for on change of each of the filter items
  // Use the clear button for common filter clearing
  const onFilterCase = useCallback(() => {
    const data = form.getFieldsValue();
    const filter = {};
    filter["client"] = data.client;
    filter["telemedicine-category"] = data["telemedicine-category"];
    filter["case-id"] = data["case-id"];
    filter["state-ids"] = data["state-ids"];
    filter["physician-id"] = data["physician-id"];
    filter["patient-id"] = data["patient-id"];
    filter["patient-first-name"] = data["patient-first-name"];
    filter["patient-last-name"] = data["patient-last-name"];
    filter["patient-email"] = data["patient-email"];
    filter["physician-name"] = data["physician-name"];
    filter["status"] = data.status;
    filter["isArchived"] = data.isArchived;
    filter["isDeleted"] = data.isDeleted;
    filter["created-date-range"] = data["created-date-range"];
    filter["updated-date-range"] = data["updated-date-range"];
    filter["exclude-cancelled"] = data["exclude-cancelled"];
    filter["tags"] = data.tags
    filter["unhandled"] = data.unhandled;

    if (data.unhandled) {
      filter["unhandledWithinSpan"] = data.unhandledWithinSpan
      filter["unhandledWithinIncrement"] = data.unhandledWithinIncrement
    }

    for(let [key, value] of Object.entries(filter)) {
      if (typeof value === 'string' && value?.trim().length === 0) {
        delete filter[key]
      }
      if (Array.isArray(value) && value.length === 0) {
        delete filter[key]
      }
      if (value === null || value === undefined) {
        delete filter[key]
      }
    }

    onFilter(filter);
  }, [form, onFilter, setSearchFilter]);


  const fetchAvailableCaseTags = () => {
    getCaseTags()
      .then(res => {
        if (res.data) {
          const { caseTags } = res.data
          setAvailableCaseTags(caseTags.map(tagObj => tagObj.tag))
        }
      })
      .catch(() => {
        message.error("Error retrieving available case tags.");
      })
  }

  const handleArchivedDeletedToggle = ({ target: { id, checked } }) => {
    if (checked) {
      form.setFieldsValue({
        isArchived: id.toLowerCase().includes('archived'),
        isDeleted: !id.toLowerCase().includes('archived'),
      })
    }
    onFilterCase()
  }

  useEffect(() => {
    fetchAvailableCaseTags()

    if (hasRestoredFilters.current) return;
    hasRestoredFilters.current = true;

    if (caseFilters) {
      form.setFieldsValue(caseFilters);
      setCases(caseSearchResults || []);
    }
  }, [form, caseFilters, caseSearchResults])

  return (
    <Form 
      form={form} 
      name="physician_details" 
      layout={"vertical"}
      onFinish={onFilterCase}
    >
      <Row gutter={24} justify="space-between">
        <Col xs={12} sm={12} md={6} lg={6} xl={5}>
          <Form.Item name="case-id" label="Case Id">
            <Input id="case-filter-case-id-input" placeholder="Filter by Case Id" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6} xl={5}>
          <Form.Item name="physician-id" label="Provider">
            <Select
              id="case-filter-physician-select"
              allowClear
              showSearch
              placeholder="Filter by Provider"
              getPopupContainer={(trigger) => trigger.parentElement}
              optionFilterProp="physicianName"
              filterOption={(input, option) =>
                option.physicianName.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option
                key='unassigned'
                value='unassigned'
                physicianName="Unassigned"
              >
                Unassigned
              </Option>
              {physicians.map((physician) => (
                <Option
                  key={physician._id}
                  value={physician._id}
                  physicianName={`${physician.firstName} ${physician.lastName}`}
                >
                  {physician.firstName} {physician.lastName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={6} lg={5} xl={7}>
          <Form.Item name="telemedicine-category" label="Category">
            <Select
              id="case-filter-category-select"
              allowClear
              showSearch
              placeholder="Filter by telemedicine category"
              getPopupContainer={(trigger) => trigger.parentElement}
              optionFilterProp="telemedicineCategory"
              filterOption={(input, option) =>
                option.telemedicineCategory.toLowerCase().includes(input.toLowerCase())
              }
            >
              {telemedicineCategory.map((c) => (
                <Option 
                  key={c.key} 
                  value={c._id}
                  telemedicineCategory={c.name}
                >
                    {c.name}
                  </Option>
                )
              )}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} justify="space-between">
        <Col xs={12} sm={12} md={6} lg={6} xl={5}>
          <Form.Item name="patient-id" label="Patient Id">
            <Input id="case-filter-patient-id-input" placeholder="Filter by Patient Id" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6} xl={5}>
          <Form.Item name="patient-first-name" label="Patient First Name">
            <Input id="case-filter-patient-first-name-input" placeholder="Filter by Patient First Name" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6} xl={5}>
          <Form.Item name="patient-last-name" label="Patient Last Name">
            <Input id="case-filter-patient-last-name-input" placeholder="Filter by Patient Last Name" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6} xl={5}>
          <Form.Item name="patient-email" label="Patient Email">
            <Input id="case-filter-patient-email-input" placeholder="Filter by Patient Email" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} justify="space-around">
        <Col xs={12} sm={12} md={12} lg={6} xl={8}>
          <Form.Item name="status" label="Status(es)">
            <Select
              id="case-filter-status-select"
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
              placeholder="Please select"
              defaultValue={[]}
            >
              {caseStatus.map(status => <Option key={status._id} value={status.name}>{status.name}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={12} lg={6} xl={8}>
          <Form.Item name="state-ids" label="State(s)">
          <Select
            id="case-filter-states-select"
            mode="multiple"
            allowClear
            showSearch
            style={{ width: '100%' }}
            placeholder="Please select"
            getPopupContainer={(trigger) => trigger.parentElement}
            defaultValue={[]}
            optionFilterProp="state"
            filterOption={(input, option) =>
              option.state.toLowerCase().includes(input.toLowerCase())
            }

          >
            {states.map(state => <Option key={state.key} state={state.name}>{state.name}</Option>)}
          </Select>
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={12} lg={5} xl={8}>
          <Form.Item name="client" label="Clients">
            <Select
              id="case-filter-client-select"
              allowClear
              placeholder="Filter by Clients"
              getPopupContainer={(trigger) => trigger.parentElement}
            >
              {clients.map(({ _id, name }) => <Option key={_id} value={_id}>{name}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} justify="space-around"> 
        <Col xs={12} sm={12} md={12} lg={5} xl={8}>
          <Form.Item
            name="created-date-range"
            label={
              <span>
                Created Date Range&nbsp;
                <Tooltip title="Dates are matched against UTC. The table also displays times in UTC. Select the UTC calendar date you want to filter by.">
                  <InfoCircleOutlined style={{ color: '#aaa' }} />
                </Tooltip>
              </span>
            }
            getValueFromEvent={(_, dateStrings) =>
              dateStrings?.[0] && dateStrings?.[1] ? dateStrings : undefined
            }
          >
            <DateRangePicker
              placeholder={["Start Date", "End Date"]}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={12} lg={5} xl={8}>
          <Form.Item
            name="updated-date-range"
            label={
              <span>
                Updated Date Range&nbsp;
                <Tooltip title="Dates are matched against UTC. The table also displays times in UTC. Select the UTC calendar date you want to filter by.">
                  <InfoCircleOutlined style={{ color: '#aaa' }} />
                </Tooltip>
              </span>
            }
            getValueFromEvent={(_, dateStrings) =>
              dateStrings?.[0] && dateStrings?.[1] ? dateStrings : undefined
            }
          >
            <DateRangePicker
              placeholder={["Start Date", "End Date"]}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
      <Col xs={14} sm={14} md={7} lg={6} xl={5}>
        <div style={{ display: 'flex', flexDirection: 'column'}}>
          <Form.Item name="isArchived" valuePropName="checked" noStyle>
            <StyledCheckbox id="case-filter-archived-checkbox" onChange={handleArchivedDeletedToggle}>Archived</StyledCheckbox>
          </Form.Item>
          <Form.Item name="isDeleted" valuePropName="checked" noStyle>
            <StyledCheckbox id="case-filter-deleted-checkbox" onChange={handleArchivedDeletedToggle}>Deleted</StyledCheckbox>
          </Form.Item>
          <Form.Item name="exclude-cancelled" valuePropName="checked" noStyle>
            <StyledCheckbox id="case-filter-exclude-cancelled-checkbox">Exclude Cancelled</StyledCheckbox>
          </Form.Item>
          <div style={{ width: '400px', display: 'flex', gap: '5px', alignItems: 'center'}}>
            <Form.Item name="unhandled" valuePropName="checked">
              <StyledCheckbox id="case-filter-unhandled-checkbox">Cases not handled within</StyledCheckbox>
            </Form.Item>
            <Form.Item name="unhandledWithinSpan" initialValue={UnhandledDefault.SPAN}>
              <Select id="case-filter-unhandled-span-select" style={{ width: '60px'}}>
                {Array.from({length: 24}, (_, i) => <Option key={i} value={i + 1}>{i + 1}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="unhandledWithinIncrement" initialValue={UnhandledDefault.INCREMENT}>
              <Select id="case-filter-unhandled-increment-select" style={{ width: '100px'}}>
                <Option key={HOUR} value={HOUR}>Hours</Option>
                <Option key={DAY} value={DAY}>Days</Option>
              </Select>
            </Form.Item>
          </div>
        </div>
        </Col>
        <Col xs={10} sm={10} md={{ span: 4, offset: 13}} lg={{ margin: 2, offset: 13}} xl={{ margin: 2, offset: 15}}>
          <ClearAllContainer>
            <StyledButton id="case-filter-clear-btn" type="secondary" onClick={clearFilter}>
              Clear all
            </StyledButton>
            <Form.Item>
              <StyledButton id="case-filter-search-btn" type="primary" htmlType="submit">
                Search
              </StyledButton>
            </Form.Item>
          </ClearAllContainer>
        </Col>
      </Row>

      <Row>
        <Col xs={12} sm={12} md={12} lg={5} xl={6}>
          <Form.Item name="tags" label="Tags">
            <Select
              id="case-filter-tags-select"
              mode="tags"
              placeholder="Filter by Case Tags"
            >
              {availableCaseTags?.map((tag, i) => <Option key={tag + i} value={tag}>{tag}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default CaseFilter;
